import { Injectable, computed, inject, signal } from '@angular/core';
import { injectQuery, queryOptions } from '@ngneat/query';
import { derivedAsync } from 'ngxtension/derived-async';
import { OrderProductService, OrderWithDispatches } from './order-product.service';
import { OrderGroupDetailPayload } from '../models/order-detail.model';

@Injectable()
export class OrderProductDialogState {
  private readonly query = injectQuery();
  private readonly orderProductService = inject(OrderProductService);

  readonly payload = signal<OrderGroupDetailPayload | null>(null);

  private getOrderProductsQueryOptions(payload: OrderGroupDetailPayload) {
    return queryOptions({
      queryKey: ['order-products', payload] as const,
      queryFn: async () => await this.orderProductService.getOrderProducts(payload),
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    });
  }

  readonly orderProductsQuery = derivedAsync(() => {
    const payload = this.payload();
    if (!payload) return Promise.resolve(null);

    return this.query({
      ...this.getOrderProductsQueryOptions(payload),
      enabled: !!payload,
    }).result$;
  });

  readonly orderGroupDetail = computed(() => this.orderProductsQuery()?.data ?? null);
  readonly orderDispatches = computed(() => {
    const detail = this.orderGroupDetail();
    return OrderProductService.processOrderDispatches(detail);
  });

  readonly isLoading = computed(() => this.orderProductsQuery()?.isLoading ?? false);
  readonly error = computed(() => this.orderProductsQuery()?.error ?? null);
  readonly hasResults = computed(() => {
    const detail = this.orderGroupDetail();
    return !!detail?.orders.length && !this.error();
  });

  search(payload: OrderGroupDetailPayload): void {
    this.payload.set(payload);
  }

  clear(): void {
    this.payload.set(null);
  }
}